import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import cardValidator from 'card-validator';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';
import { PaymentForm } from '@/shared/components/payment-form';
import { IPaymentFormData, ReviewCompleteForm } from '@/shared/components/payment-form/constants';
import { selectPaymentInfoPopupShown } from '@/store/slices/auth/selectors';
import { markPaymentInfoPopupShown } from '@/store/slices/auth';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { selectOnboardingUser, selectCreditCards } from '@/store/slices/onboarding/selectors';
import { AuthCardCreateRequest } from '@/shared/endpoints/customers';
import { CustomersController } from '@/shared/endpoints/customers';
import { setPageType } from '@/shared/helpers/page-type';
import { useHeaderHeight } from '@/shared/components/header-height-context';
import { Alert, Button, Modal } from '@/shared/ui';

import CardList from './components/cardList';
import CopaysQuestions from './components/copays-questions';
import AddCardSuccess from './components/add-card-success';
import { paymentMethodNote } from './constants';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import AlertSuccessIcon from '@/assets/icons/alert_success.svg';
import CheckIcon from '@/assets/icons/check_smooth.svg';
import authorizeNetInstance, { CardData } from '../../../../../../services/authorizenet';
import './style.css';
import CardPrompt from '@/assets/icons/card-prompt.svg';
import Arrow from '@/assets/icons/arrow-payment.svg';
import { buildAddressString } from '@/shared/helpers/address';


export interface Response {
    opaqueData: {
        dataDescriptor: string;
        dataValue: string;
    };
    messages: {
        resultCode: string;
        message: {
            code: string;
            text: string;
        }[];
    };
}

export interface IProps {
    paymentMethodError: boolean;
    formikControl?: ReturnType<typeof useFormikWrapper<ReviewCompleteForm>>;
    onPaymentFormChange?: () => void;
    addPaymentOpen: boolean;
    openPaymentForm: () => void;
    closePaymentForm: () => void;
}

const AddPaymentMethodButton = ({ text, onClick, testId, loading }: { text: string; onClick?: () => void; testId?: string; loading?: boolean }) => (
    <button
        className="relative flex justify-center items-center gap-[10px] px-[45px] py-[15px] bg-aqua-500 rounded-[24px] cursor-pointer"
        onClick={onClick}
        disabled={loading}
    >
        <div className={`flex items-center gap-[10px]`}>
            <span className="font-bold text-white text-18 leading-[105%]">{text}</span>
            <img src={Arrow} width={21} alt="Arrow icon" />
        </div>
    </button>
);

export const AddCardExpectationDetails = ({ label, labelClassName }: { label?: string, labelClassName?: string }) => {
    const details = [
        'You’ll only be charged if there is a copay or OTC purchase',
        '$0 copay = no charge to you',
        'FREE subscription includes: MedBox pill dispenser, pill sorting, and monthly delivery'
    ]


    return (
        <div className='flex flex-col bg-primary-1300 p-[1.5625rem] rounded-xl gap-[1rem]'>
            {label && <span className={`${labelClassName} text-18 font-dmSansBold font-bold leading-[133%] sm:leading-[138%]`}>{label}</span>}
            {details.map((detail, index) => (
                <div key={index} className='flex flex-row items-center justify-center font-dmSans gap-[10px]'>
                    <div className='h-[23px] w-[23px] flex items-center justify-center'>
                        <img src={CheckIcon} alt='Check Icon' className='p-1.5 bg-mint-400 rounded-full' />
                    </div>
                    <div className='flex-1'><span className='text-16 block leading-[150%]'>{detail}</span></div>
                </div>
            ))}
        </div>
    )
}

const ReviewPayment = ({
    formikControl,
    paymentMethodError,
    onPaymentFormChange,
    addPaymentOpen,
    openPaymentForm,
    closePaymentForm,
}: IProps) => {
    const formFocusedRef = useRef(false);
    const dispatch = useAppDispatch();
    const headerHeight = useHeaderHeight();
    const currentUser = useAppSelector(selectOnboardingUser)!;
    const paymentInfoPopupShown = !!useAppSelector(selectPaymentInfoPopupShown);
    const existingCreditCards = useAppSelector(selectCreditCards);
    const [addPaymentLoading, setAddPaymentLoading] = useState(false);
    const [isCopaysQuestionsOpen, setIsCopaysQuestionsOpen] = useState(false);
    const [cardSuccessOpen, setCardSuccessOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cardToReplace, setCardToReplace] = useState<import('@/shared/endpoints/customers').ICreditCard | null>(null);

    const _conv_page_type = existingCreditCards.length ? 'payments_credit_cards' : 'payment_form';
    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (paymentInfoPopupShown || existingCreditCards.length) return;
            if (!paymentInfoPopupShown && !formFocusedRef.current) {
                dispatch(markPaymentInfoPopupShown());
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [dispatch, paymentInfoPopupShown, existingCreditCards.length]);


    const relevantAddress = useMemo(() => {
        if (!currentUser.addresses) return null;
        const relevantAddy = currentUser.addresses.find(address => address.preferred) || currentUser.addresses[0];
        return relevantAddy;
    }, [currentUser.addresses]);

    const addressStringForPaymentForm = useMemo(() => {
        return buildAddressString(relevantAddress);
    }, [relevantAddress]);


    const handleSave = useCallback(
        async (formData: IPaymentFormData) => {
            try {
                const { addressLine1, addressLine2, unit, city, state, zip } = relevantAddress!;
                if (formData.hasDifferentBillingAddress === 1) {
                    formData.billingDetails = {
                        ...formData.billingDetails,
                        addressLine1: addressLine1!,
                        addressLine2: addressLine2!,
                        unit: unit || '',
                        city,
                        state,
                        zip
                    };
                }
                setAddPaymentLoading(true);
                const [month, year] = formData.expirationDate.split('/');
                const fullName = `${formData?.firstName} ${formData?.lastName}`;
                const cardData: CardData = {
                    cardNumber: formData.cardNumber.toString().replaceAll(' ', ''),
                    month,
                    year,
                    cardCode: formData.cvv.toString(),
                    fullName,
                    zip: formData?.billingDetails?.zip || ''
                };

                const response = await authorizeNetInstance.post(cardData);
                const brand = cardValidator.number(formData.cardNumber)?.card?.type;
                const payload: AuthCardCreateRequest = {
                    cardId: response.opaqueData?.dataValue,
                    name: fullName,
                    lastFourDigit: formData.cardNumber.slice(-4),
                    expiryMonth: formData.expirationDate.split('/')[0],
                    expiryYear: formData.expirationDate.split('/')[1],
                    brand,
                    preferred: !!formData.preferred,
                    accountId: currentUser?.id,
                    billingDetails: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        ...formData.billingDetails
                    }
                };

                const result = await dispatch(CustomersController.addPaymentMethod(payload));
                if (!result?.type?.includes('fulfilled')) {
                    throw new Error('Error adding payment method');
                }

                if (cardToReplace) {
                    const freshCustomerResult = await dispatch(CustomersController.get());
                    if (CustomersController.get.fulfilled.match(freshCustomerResult)) {
                        const freshCard = freshCustomerResult.payload.creditCards.find(c => c.id === cardToReplace.id);
                        if (freshCard) {
                            await dispatch(CustomersController.paymentMethodDelete(freshCard));
                        }
                    }
                    setCardToReplace(null);
                }

                closePaymentForm();
                setCardSuccessOpen(true);
            } catch (error) {
                const errorMessageCaught = (error as Error)?.message;
                setErrorMessage(
                    errorMessageCaught === ERROR_MESSAGES.CARD_ALREADY_ASSOCIATED
                        ? errorMessageCaught
                        : ERROR_MESSAGES.ADD_PAYMENT_METHOD_FAILED
                );
            } finally {
                setAddPaymentLoading(false);
            }
        },
        [currentUser.addresses, currentUser?.id, dispatch, closePaymentForm, cardToReplace]
    );

    useEffect(() => {
        if (formikControl?.values.nowOrLater) {
            setErrorMessage('');
        }

    }, [formikControl?.values.nowOrLater])


    const CardOrSpawnAddCardUI = useMemo(() => {
        return (
            <div className={`flex flex-col p-[20px] gap-[20px] shadow-paymentOptions rounded-xl bg-grey-120 ${paymentMethodError ? 'border border-red-500' : ''}`}>
                <div className='flex flex-col lms:flex-row gap-[10px] lms:gap-[20px] itelms-center justify-center lms:justify-start lms:items-center'>
                    <img src={CardPrompt} alt="Add Card Prompt" className="w-[100px] h-[100px] lms:w-[120px] lms:h-[120px]" />
                    <div className='flex flex-col text-center lms:text-start gap-[10px] lms:gap-[5px]'>
                        <div className='font-atkinsonBold font-bold text-24 leading-[130%]'>Activate Your MedBox Subscription</div>
                        <div className='font-dmSans text-18 sm:text-16 leading-[133%] sm:leading-[125%] sm:letter-spacing-[-0.16px]'>Keep your monthly refills coming <br /><span className='font-dmSansBold font-bold'>A card on file is required</span></div>
                    </div>
                </div>
                {!!existingCreditCards.length && <CardList onChange={(card) => { setCardToReplace(card); openPaymentForm(); }} className="" list={existingCreditCards} loading={addPaymentLoading} />}
                <AddCardExpectationDetails label='Our Guarantee:' />
                {!existingCreditCards.length && <AddPaymentMethodButton onClick={openPaymentForm} text={'Add Payment Method'} />}
                {paymentMethodError && (
                    <Alert
                        className=''
                        label={
                            <span className="font-normal font-atkinsonHyperlegible">
                                <span className="font-atkinsonBold">Credit Card is Required</span>
                                <br />
                                {paymentMethodNote}
                            </span>
                        }
                        variant="error"
                    />
                )}
                <Alert
                    labelClassName='font-dmSansBold leading-[137%] text-16 !font-bold'
                    label='If you have a copay or OTC purchase, your card will only be charged after your prescriptions have shipped.'
                    variant='infoV2'
                    className=''
                    boldLabel={false}
                />
            </div>
        )
        // }
    }, [existingCreditCards, paymentMethodError, openPaymentForm]);



    return (
        <div className="flex flex-col">
            <div>
                {CardOrSpawnAddCardUI}
            </div>
            <Button className="mr-auto mt-[20px] sm:mt-[30px]" variant="text" onClick={() => setIsCopaysQuestionsOpen(true)}>
                <div className='flex flex-row items-center gap-2'>
                    <img src={AlertSuccessIcon} alt='Check Icon' />
                    <span className="underline font-dmSansBold font-medium text-17 text-primary text-start leading-[130%]">Questions about copays?</span>
                </div>
            </Button>

            <Modal
                open={addPaymentOpen}
                onClose={closePaymentForm}
                contentClassName="!py-[20px] !px-[20px] sm:!py-[30px] sm:!px-[30px] !max-w-[500px] w-full"
            >
                <PaymentForm
                    withinModal
                    onClose={closePaymentForm}
                    hideIconsFromInput
                    awarenessText=""
                    className="payment-form-onboarding"
                    preferredSelectedTrueRequired
                    userAddress={addressStringForPaymentForm}
                    addPaymentLoading={addPaymentLoading}
                    onSave={handleSave}
                    headerOffset={headerHeight}
                    onPaymentFormChange={() => {
                        onPaymentFormChange?.();
                        setErrorMessage('');
                    }}
                    errorMessage={errorMessage}
                />
            </Modal>

            <CopaysQuestions
                open={isCopaysQuestionsOpen}
                onClose={() => setIsCopaysQuestionsOpen(false)}
            />

            <AddCardSuccess
                open={cardSuccessOpen}
                onContinue={() => setCardSuccessOpen(false)}
            />
        </div >
    );
};

export default ReviewPayment;